'use client';

import { Radius } from '@ghosts-inc/design-system/constants';
import { useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import { WishlistsWithMetadata } from '../../wishlist/types';
import { FlatBuyerPreferences } from '../types';
import { AddToWishlistButton } from './AddToWishlistButton';
import { AddToWishlistTypography } from './AddToWishlistTypography';
import { MoreRecommendationsTypography } from './MoreRecommendationsTypography';

type Props = {
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  numberOfItems: number;
  preferencesPromise: Promise<FlatBuyerPreferences>;
  wishlistsPromise: Promise<WishlistsWithMetadata>;
};

const NUMBER_OF_HITS_PER_ROW = {
  mobile: 2,
  smallTablet: 3,
  tablet: 3,
  desktop: 4,
};

export const AddToWishlistCard = ({
  categoriesPromise,
  numberOfItems,
  preferencesPromise,
  wishlistsPromise,
}: Props) => {
  const isSmallTabletOrBelow = useMediaQuery((theme: Theme) => theme.breakpoints.down('tablet'));

  return (
    <Grid size={{ mobile: 2, smallTablet: 4, desktop: 3 }} role="grid">
      <Stack
        bgcolor="surfaceLight.soft"
        border={1}
        borderColor="lightBorder.default"
        borderRadius={Radius.large}
        height="100%"
        maxHeight={360}
        py={{
          // If "AddToWishlistCard" is the only card in the row, it should have extra padding
          // so that it looks centered and similar to the other cards.
          mobile: numberOfItems % NUMBER_OF_HITS_PER_ROW.mobile === 0 ? 4 : 3,
          smallTablet: numberOfItems % NUMBER_OF_HITS_PER_ROW.smallTablet === 0 ? 5 : 3,
          tablet: numberOfItems % NUMBER_OF_HITS_PER_ROW.tablet === 0 ? 6 : 3,
          desktop: numberOfItems % NUMBER_OF_HITS_PER_ROW.desktop === 0 ? 8 : 3,
        }}
        justifyContent="center"
        data-testid="add-to-wishlist-card"
      >
        <Stack zIndex={1} px={{ mobile: 2, tablet: 3 }}>
          <MoreRecommendationsTypography
            color="typographyDarkSecondary"
            mb={{ mobile: 2, smallTablet: 1, tablet: 2 }}
            textAlign="center"
            variant="bodyLargeLight"
          />
          <AddToWishlistTypography
            variant={isSmallTabletOrBelow ? 'bodyLargeHeavy' : 'h2'}
            mb={{ mobile: 4, smallTablet: 2, tablet: 4 }}
            textAlign="center"
          />
          <AddToWishlistButton
            categoriesPromise={categoriesPromise}
            preferencesPromise={preferencesPromise}
            size={isSmallTabletOrBelow ? 'small' : 'large'}
            wishlistsPromise={wishlistsPromise}
          />
        </Stack>
      </Stack>
    </Grid>
  );
};
